import { useEffect, useRef, useState } from "react";
import { CalendarDays, X, Package, ChevronDown, ChevronUp } from "lucide-react";
import Portal from "./Portal.jsx";
import "../../../css/OnRoad.css";

export default function TimelineModal({ open, onClose, manifiesto, timelineData }) {
    const backdropRef = useRef(null);
    const dialogRef = useRef(null);
    const lastActiveRef = useRef(null);

    // Estado para controlar grupos expandidos
    const [gruposExpandidos, setGruposExpandidos] = useState({});

    // Bloquear scroll y gestionar foco
    useEffect(() => {
        if (!open) return;
        lastActiveRef.current = document.activeElement;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        // foco inicial
        const t = setTimeout(() => {
            const focusable = dialogRef.current?.querySelector?.(
                'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
            );
            (focusable || dialogRef.current)?.focus?.();
        }, 0);

        return () => {
            clearTimeout(t);
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
            lastActiveRef.current?.focus?.();
        };
    }, [open, onClose]);

    // Auto-expandir todos los grupos al abrir la modal
    // useEffect(() => {
    //     if (open && timelineData?.eventosAgrupados) {
    //         const todosExpandidos = {};
    //         timelineData.eventosAgrupados.forEach(grupo => {
    //             todosExpandidos[grupo.grupoId] = true;
    //         });
    //         setGruposExpandidos(todosExpandidos);
    //     }
    // }, [open, timelineData]);

    if (!open) return null;

    const handleBackdropClick = (e) => {
        if (e.target === backdropRef.current) onClose?.();
    };

    // Toggle de expansión de grupo
    const toggleGrupo = (grupoId) => {
        setGruposExpandidos(prev => ({
            ...prev,
            [grupoId]: !prev[grupoId]
        }));
    };

    // Función para formatear fecha y hora
    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        
        try {
            const date = new Date(timestamp);
            const pad = n => n.toString().padStart(2, '0');
            
            return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
        } catch {
            return 'Fecha inválida';
        }
    };

    const getEventIcon = (evento) => {
        const icons = {
            'LLEGADA': '📍',
            'SALIDA': '🏁'
        };
        return icons[evento] || '📋';
    };

    const getPhaseColor = (fase) => {
        const colors = {
            'CARGUE': 'var(--onr-primary)',
            'DESCARGUE': 'var(--onr-success)',
            'EN_RUTA': 'var(--onr-warning)'
        };
        return colors[fase] || 'var(--onr-muted)';
    };

    // Obtener progreso porcentual
    const progresoPercentual = Math.round((timelineData?.progreso || 0) * 100);
    const tieneEventos = timelineData?.eventosAgrupados?.some(grupo => grupo.eventos.length > 0);

    return (
        <Portal>
            <div
                className="sheet-backdrop"
                ref={backdropRef}
                onMouseDown={handleBackdropClick}
                aria-hidden="true"
            >
                <div
                    className="sheet-container timeline-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Flujo ${manifiesto}`}
                    tabIndex={-1}
                    ref={dialogRef}
                >
                    {/* Header */}
                    <div className="sheet-header">
                        <div className="sheet-title">
                            <CalendarDays size={20} />
                            Flujo Manifiesto  – {manifiesto}
                        </div>
                        <button
                            className="sheet-close-btn"
                            onClick={onClose}
                            aria-label="Cerrar"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    
                    {/* Content */}
                    <div className="sheet-content sheet-content-timeline">
                        {/* Timeline Section - CON SCROLL POR GRUPO */}
                        <div className="timeline-events-section">
                            <label className="section-label-compact">
                                Eventos por Grupo de Remesas
                            </label>
                            
                            <div className="timeline-grupos-container">
                                {tieneEventos ? (
                                    timelineData.eventosAgrupados.map((grupo) => (
                                        <div key={grupo.grupoId} className="timeline-grupo-wrapper">

                                            <div className="grupo-status-indicator"/>

                                            {/* Header del Grupo - CLICKEABLE */}
                                            <div 
                                                className={`timeline-grupo-header-clickable ${gruposExpandidos[grupo.grupoId] ? 'expanded' : ''}`}
                                                onClick={() => toggleGrupo(grupo.grupoId)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        toggleGrupo(grupo.grupoId);
                                                    }
                                                }}
                                            >
                                                <div className="grupo-header-content">
                                                    {/* NUEVO: Icono en container circular */}
                                                    <div className="grupo-icon-container">
                                                        <Package size={20} />
                                                    </div>
                                                    <div className="grupo-info-content">
                                                        {/* Ruta principal */}
                                                        <div className="grupo-ruta-info">
                                                            <span className="grupo-ruta-text">
                                                                {grupo.origen} → {grupo.destino}
                                                            </span>
                                                        </div>
                                                        
                                                        {/* NUEVO: Metadata mejorada */}
                                                        <div className="grupo-metadata-enhanced">
                                                            {/* Badge de remesas */}
                                                            <div className="metadata-badge remesas-badge">
                                                                <span className="badge-icon">📦</span>
                                                                <span className="badge-text">
                                                                    {grupo.cantidadRemesas} remesa{grupo.cantidadRemesas !== 1 ? 's' : ''}
                                                                </span>
                                                            </div>
                                                            
                                                            {/* Badge de progreso con porcentaje */}
                                                            <div className="metadata-badge progreso-badge">
                                                                <span className="badge-icon">✓</span>
                                                                <span className="badge-text">
                                                                    {grupo.eventos.length}/4 eventos
                                                                </span>
                                                                <span className="badge-percentage">
                                                                    ({Math.round((grupo.eventos.length / 4) * 100)}%)
                                                                </span>
                                                            </div>
                                                            
                                                            {/* NUEVO: Badge de estado */}
                                                            <div className={`metadata-badge estado-badge ${
                                                                grupo.eventos.length === 4 ? 'completado' :
                                                                grupo.eventos.length > 0 ? 'en-proceso' : 'pendiente'
                                                            }`}>
                                                                {grupo.eventos.length === 4 ? '🎉 Completo' :
                                                                grupo.eventos.length > 0 ? '⏳ En Proceso' : '⚪ Pendiente'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Icono de expansión mejorado */}
                                                <div className="grupo-expand-icon-enhanced">
                                                    {gruposExpandidos[grupo.grupoId] ? (
                                                        <ChevronUp size={22} strokeWidth={2.5} />
                                                    ) : (
                                                        <ChevronDown size={22} strokeWidth={2.5} />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Eventos del Grupo - CON SCROLL PROPIO */}
                                            {gruposExpandidos[grupo.grupoId] && (
                                                <div className="timeline-grupo-eventos-scrollable">
                                                    {grupo.eventos.length > 0 ? (
                                                        grupo.eventos.map((evento, eventoIndex) => (
                                                            <div 
                                                                key={`${grupo.grupoId}-${evento.evento}-${eventoIndex}`} 
                                                                className="timeline-event-item-detailed"
                                                            >
                                                                {/* Header del Evento */}
                                                                <div className="timeline-event-header">
                                                                    <div className="timeline-event-icon">
                                                                        <span className="event-emoji">
                                                                            {getEventIcon(evento.evento)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="timeline-event-info">
                                                                        <div className="timeline-event-title">
                                                                            {evento.evento}
                                                                            <span 
                                                                                className="timeline-phase-badge"
                                                                                style={{ 
                                                                                    backgroundColor: getPhaseColor(evento.fase) 
                                                                                }}
                                                                            >
                                                                                {evento.fase}
                                                                            </span>
                                                                        </div>
                                                                        <div className="timeline-event-time">
                                                                            {formatDateTime(evento.timestamp)}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* NUEVO: Detalles de Remesas del Evento */}
                                                                {evento.remesas && evento.remesas.length > 0 && (
                                                                    <div className="timeline-event-remesas">
                                                                        <div className="event-remesas-header">
                                                                            <span className="remesas-label">
                                                                                Remesas ({evento.remesas.length})
                                                                            </span>
                                                                        </div>
                                                                        <div className="event-remesas-list">
                                                                            {evento.remesas.map((remesa, index) => (
                                                                                <div 
                                                                                    key={`${grupo.grupoId}-${evento.evento}-remesa-${index}`}
                                                                                    className="event-remesa-chip"
                                                                                >
                                                                                    <span className="remesa-chip-numero">
                                                                                        #{remesa.consecutivo}
                                                                                    </span>
                                                                                    <span className="remesa-chip-producto">
                                                                                        {remesa.producto}
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="timeline-grupo-sin-eventos">
                                                            <p>No hay eventos reportados para este grupo</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="timeline-empty-state">
                                        <CalendarDays size={32} className="text-muted" />
                                        <p>No hay eventos registrados aún</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Progress Section */}
                        <div className="timeline-progress-section">
                            <label className="section-label-compact">Progreso del Manifiesto</label>
                            <div className="progress-container">
                                <div className="progress-bar-container">
                                    <div 
                                        className="progress-bar-fill"
                                        style={{ 
                                            width: `${progresoPercentual}%`,
                                            transition: 'width 0.5s ease-in-out'
                                        }}
                                    />
                                </div>
                                <div className="progress-percentage">
                                    {progresoPercentual}%
                                </div>
                            </div>
                            {timelineData?.eventosAgrupados && (
                                <div className="progress-details">
                                    <span className="progress-detail-text">
                                        {timelineData.eventosAgrupados.reduce((acc, g) => acc + g.eventos.length, 0)} eventos reportados de {timelineData.eventosAgrupados.length * 4} totales
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
}